import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import styled from 'styled-components';

import { TrophyIcon } from '../../../assets/svg/SVGComponents';
import { IBoardRankingGroup } from '../../../dtos/IBoardSnapshot';
import { podiums } from '../logic/final';
import { groupKey, groupTitle } from '../logic/labels';
import { colors, IconText, PositionDisc } from '../styles';

const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 18rem), 1fr));
  gap: 1.2vh 1.2vw;
  margin-bottom: 1.6vh;
`;

const Card = styled(motion.section)`
  padding: 1.2vh 1vw;
  border-radius: 1rem;
  background: rgba(255, 200, 61, 0.1);
  border: 1px solid rgba(255, 200, 61, 0.45);

  h2 {
    margin: 0 0 0.8vh;
    font-family: 'RedHatDisplay-Bold';
    font-size: clamp(0.9rem, 2.1vh, 1.6rem);
    color: ${colors.gold};
  }

  ol {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0.6vh;
  }

  li {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.8vw;
    font-size: clamp(0.95rem, 2.4vh, 1.9rem);
  }

  strong {
    font-family: 'RedHatDisplay-Bold';
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span:last-child {
    font-family: 'RedHatDisplay-Bold';
    font-variant-numeric: tabular-nums;
    color: ${colors.accent};
  }
`;

interface IFinalPodiumProps {
  groups: IBoardRankingGroup[];
}

/**
 * Tela final fixa (FE-77): o pódio de cada categoria acima da classificação
 * completa. Aparece também para quem abre o telão depois do fim.
 */
const FinalPodium: React.FC<IFinalPodiumProps> = ({ groups }) => {
  const reduce = useReducedMotion();
  const list = podiums(groups);
  if (list.length === 0) return null;

  return (
    <Strip aria-label="Pódio">
      {list.map(({ group, places }, index) => (
        <Card
          key={groupKey(group)}
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            type: 'spring',
            bounce: 0,
            duration: 0.6,
            delay: index * 0.12,
          }}
        >
          <h2>
            <IconText>
              <TrophyIcon strokeWidth={2} />
              {groupTitle(group)}
            </IconText>
          </h2>
          <ol>
            {places.map(place => (
              <li key={place.team_id}>
                <PositionDisc $position={place.position ?? 0}>
                  {place.position}
                </PositionDisc>
                <strong>{place.team_name}</strong>
                <span>{place.points} pts</span>
              </li>
            ))}
          </ol>
        </Card>
      ))}
    </Strip>
  );
};

export default FinalPodium;
